import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import * as XLSX from "xlsx";
import { ADMIN_MONTHLY_OVERVIEW } from "../monthly-overview/api/admin-monthly-overview";
import {
  ADMIN_MONTHLY_SETTLEMENT_PAYOUTS,
  ADMIN_DELETE_MONTHLY_SETTLEMENT_PAYOUT,
} from "./api/advance-ledger";
import type {
  AdminMonthlyOverviewData,
  AdminMonthlyOverviewVars,
} from "../monthly-overview/model/types";
import type {
  AdminMonthlySettlementPayout,
  AdminMonthlySettlementPayoutsData,
  AdminMonthlySettlementPayoutsVars,
  AdminDeleteMonthlySettlementPayoutData,
  AdminDeleteMonthlySettlementPayoutVars,
} from "./model/types";
import { notifyError, notify } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";

function getCurrentYearMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatKRW(value: number): string {
  return value.toLocaleString("ko-KR");
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

interface LedgerRow {
  date: string; // "2026.04" or "2026.05.04"
  label: string;
  increase: number | null;
  decrease: number | null;
  balance: number;
  payoutId?: number;
  payoutKind?: AdminMonthlySettlementPayout["kind"];
}

export default function AdvanceLedger() {
  const [yearMonth, setYearMonth] = useState(getCurrentYearMonth());

  const { data: overviewData, loading: overviewLoading } = useQuery<
    AdminMonthlyOverviewData,
    AdminMonthlyOverviewVars
  >(ADMIN_MONTHLY_OVERVIEW, {
    variables: { yearMonth },
    onError: (e) => notifyError(e.message || "월별 데이터 오류"),
  });

  const {
    data: payoutsData,
    loading: payoutsLoading,
    refetch: refetchPayouts,
  } = useQuery<AdminMonthlySettlementPayoutsData, AdminMonthlySettlementPayoutsVars>(
    ADMIN_MONTHLY_SETTLEMENT_PAYOUTS,
    {
      variables: { yearMonth },
      onError: (e) => notifyError(e.message || "정산금 지급 데이터 오류"),
    }
  );

  const [deletePayout] = useMutation<
    AdminDeleteMonthlySettlementPayoutData,
    AdminDeleteMonthlySettlementPayoutVars
  >(ADMIN_DELETE_MONTHLY_SETTLEMENT_PAYOUT);

  const overview = overviewData?.adminMonthlyOverview;
  const payouts: AdminMonthlySettlementPayout[] =
    payoutsData?.adminMonthlySettlementPayouts ?? [];

  // 장부 행 계산
  const ledgerRows: LedgerRow[] = [];
  const yearMonthLabel = yearMonth.replace("-", ".");
  let balance = 0;

  if (overview) {
    const actualAmount = overview.actualAmount;
    const couponSubsidy = overview.totalAmount - overview.actualAmount;

    balance += actualAmount;
    ledgerRows.push({
      date: yearMonthLabel,
      label: "거래액 합계",
      increase: actualAmount,
      decrease: null,
      balance,
    });

    balance += couponSubsidy;
    ledgerRows.push({
      date: yearMonthLabel,
      label: "쿠폰 보전금(마히 부담)",
      increase: couponSubsidy,
      decrease: null,
      balance,
    });

    balance -= overview.consumerFee;
    ledgerRows.push({
      date: yearMonthLabel,
      label: "소비자 수수료",
      increase: null,
      decrease: overview.consumerFee,
      balance,
    });

    balance -= overview.platformFee;
    ledgerRows.push({
      date: yearMonthLabel,
      label: "사장님 수수료",
      increase: null,
      decrease: overview.platformFee,
      balance,
    });
  }

  for (const payout of payouts) {
    const kind = payout.kind ?? "OUTFLOW";
    const isCarryForward = kind === "CARRY_FORWARD";
    if (!isCarryForward) {
      balance -= payout.amount;
    }

    ledgerRows.push({
      date: formatDate(payout.paidAt),
      label: payout.memo ? `정산금 지급 (${payout.memo})` : "정산금 지급",
      increase: null,
      decrease: payout.amount,
      balance,
      payoutId: payout.id,
      payoutKind: kind,
    });
  }

  const loading = overviewLoading || payoutsLoading;

  const handleDelete = async (id: number) => {
    if (!window.confirm("이 정산금 지급 기록을 삭제하시겠습니까?")) return;
    try {
      await deletePayout({ variables: { id } });
      await refetchPayouts();
      notify("삭제되었습니다.");
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "삭제에 실패했습니다.");
    }
  };

  const handleDownloadExcel = () => {
    if (ledgerRows.length === 0) {
      notifyError("다운로드할 데이터가 없습니다.");
      return;
    }

    const sheetData = [
      ["구분", "내용", "증가", "차감", "잔액"],
      ...ledgerRows.map((row) => [
        row.date,
        row.label,
        row.increase ?? "",
        row.decrease ?? "",
        row.balance,
      ]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(sheetData),
      "선수금장부"
    );
    XLSX.writeFile(wb, `선수금장부_${yearMonth}.xlsx`);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="mb-1 text-2xl font-bold">선수금 장부</h1>
          <p className="text-sm text-gray-500">
            월별 거래액, 쿠폰 보전금, 수수료, 정산금 지급을 한눈에 확인합니다.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDownloadExcel}
          disabled={loading || ledgerRows.length === 0}
          className="shrink-0 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          엑셀 다운로드
        </button>
      </div>

      {/* 월 선택 */}
      <div className="mb-6 flex items-center gap-3">
        <label htmlFor="yearMonth" className="text-sm font-medium text-gray-700">
          조회 월
        </label>
        <input
          id="yearMonth"
          type="month"
          value={yearMonth}
          onChange={(e) => setYearMonth(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* 장부 테이블 */}
      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      ) : (
        <div className="mb-8 overflow-hidden rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">구분</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">내용</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">증가</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">차감</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">잔액</th>
                <th className="w-16 px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ledgerRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
              {ledgerRows.map((row, idx) => {
                const isPayoutRow = row.payoutId !== undefined;
                const isCarryForwardRow = row.payoutKind === "CARRY_FORWARD";
                const isLastRow = idx === ledgerRows.length - 1;
                const isZeroBalance = isLastRow && row.balance === 0;

                return (
                  <tr
                    key={row.payoutId ?? idx}
                    className={
                      isCarryForwardRow
                        ? "bg-amber-50"
                        : isPayoutRow
                          ? "bg-orange-50"
                          : idx < 2
                            ? "bg-blue-50/40"
                            : ""
                    }
                  >
                    <td className="px-4 py-3 text-gray-500">{row.date}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span>{row.label}</span>
                      {isCarryForwardRow && (
                        <span className="mt-1 block text-xs font-normal text-gray-500">
                          미출금 분 · 순잔액은 실지급 차감만 반영해 동일하게 유지됩니다
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-600">
                      {row.increase !== null ? formatKRW(row.increase) : ""}
                    </td>
                    <td
                      className={`px-4 py-3 text-right ${isCarryForwardRow ? "font-medium text-amber-900" : "text-red-500"}`}
                    >
                      {row.decrease !== null ? formatKRW(row.decrease) : ""}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${isZeroBalance ? "text-green-600" : "text-gray-900"}`}
                    >
                      {formatKRW(row.balance)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isPayoutRow && (
                        <button
                          type="button"
                          onClick={() => handleDelete(row.payoutId!)}
                          className="text-xs text-red-400 hover:text-red-600"
                        >
                          삭제
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
